import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getStore } from '@/lib/store';

const LANG_PRIORITY = ['de', 'en', 'it', 'fr', 'sq'];

// Resolves an image URL from Supabase-hydrated page content (with multi-language
// fallback), defaulting to the provided URL when no override exists.
export function useAdminImage(pageSlug: string, imageKey: string, defaultUrl: string): string {
  const { i18n } = useTranslation();
  const [imageUrl, setImageUrl] = useState(defaultUrl);

  const resolveImage = useCallback(() => {
    const content = getStore().content;

    const lookup = (lang: string): string | undefined => {
      const map = content[`${pageSlug}_${lang}`];
      const val = map?.[imageKey];
      return val && val !== defaultUrl ? val : undefined;
    };

    // Current language first, then the priority fallbacks.
    const current = lookup(i18n.language);
    if (current) { setImageUrl(current); return; }
    for (const lang of LANG_PRIORITY) {
      if (lang === i18n.language) continue;
      const val = lookup(lang);
      if (val) { setImageUrl(val); return; }
    }
    setImageUrl(defaultUrl);
  }, [pageSlug, imageKey, defaultUrl, i18n.language]);

  useEffect(() => {
    resolveImage();
    const handler = () => resolveImage();
    window.addEventListener('admin-content-updated', handler);
    return () => window.removeEventListener('admin-content-updated', handler);
  }, [resolveImage]);

  return imageUrl;
}
